import {
    Alert,
    Link,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useSearchParams } from "react-router";

import { Period, ResourceSummary } from "@rossum/upcloud-costs-shared";

import { fetchPeriods } from "../../api/cost-tree-api";
import { fetchResources } from "../../api/resources-api";
import { AsyncContent } from "../../common/AsyncContent";
import { formatMoney } from "../../common/format";
import { useAsync } from "../../common/use-async";
import { MonthSelect } from "../cost-tree/MonthSelect";
import { defaultMonth } from "../cost-tree/default-month";
import { AttributionValue } from "./AttributionValue";

/** Resources of a month with an unassigned tenant, service, or environment, to be labelled in UpCloud. */
export function UnassignedResourcesPage() {
    const { t } = useTranslation();
    const periods = useAsync(fetchPeriods, []);
    return (
        <Stack spacing={2}>
            <Typography variant="h5" component="h2">
                {t("unassigned.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                {t("unassigned.description")}
            </Typography>
            <AsyncContent state={periods}>
                {({ periods: list }) =>
                    list.length === 0 ? (
                        <Alert severity="info">{t("costTree.noData")}</Alert>
                    ) : (
                        <UnassignedList periods={list} />
                    )
                }
            </AsyncContent>
        </Stack>
    );
}

function UnassignedList({ periods }: { periods: readonly Period[] }) {
    const { t } = useTranslation();
    const [params, setParams] = useSearchParams();
    const month = params.get("month") ?? defaultMonth(periods) ?? "";
    const currency = periods.find((period) => period.month === month)?.currency ?? null;
    const resources = useAsync(() => fetchResources({ from: month, to: month, unassignedOnly: true }), [month]);
    return (
        <Stack spacing={2}>
            <MonthSelect
                label={t("resource.month")}
                value={month}
                periods={periods}
                onChange={(value) => {
                    setParams({ month: value });
                }}
            />
            <AsyncContent state={resources}>
                {({ resources: list }) =>
                    list.length === 0 ? (
                        <Alert severity="success">{t("unassigned.none")}</Alert>
                    ) : (
                        <ResourceTable resources={list} currency={currency} />
                    )
                }
            </AsyncContent>
        </Stack>
    );
}

function ResourceTable({ resources, currency }: { resources: readonly ResourceSummary[]; currency: string | null }) {
    const { t, i18n } = useTranslation();
    return (
        <TableContainer component={Paper}>
            <Table size="small" aria-label={t("unassigned.title")}>
                <TableHead>
                    <TableRow>
                        <TableCell>{t("resource.name")}</TableCell>
                        <TableCell>{t("resource.type")}</TableCell>
                        <TableCell>{t("resource.zone")}</TableCell>
                        <TableCell align="right">{t("resource.amount")}</TableCell>
                        <TableCell>{t("resource.tenant")}</TableCell>
                        <TableCell>{t("resource.service")}</TableCell>
                        <TableCell>{t("resource.environment")}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {resources.map((resource) => (
                        <TableRow key={resource.cloudResourceId} hover>
                            <TableCell>
                                <Link component={RouterLink} to={`/resources/${resource.cloudResourceId}`}>
                                    {resource.name ?? resource.resourceId}
                                </Link>
                            </TableCell>
                            <TableCell>
                                {t(`category.${resource.category}`, { defaultValue: resource.category })}
                            </TableCell>
                            <TableCell>{resource.zone ?? "–"}</TableCell>
                            <TableCell align="right">{formatMoney(resource.amount, currency, i18n.language)}</TableCell>
                            <TableCell>
                                <AttributionValue
                                    value={resource.attribution.tenant}
                                    source={resource.attribution.tenantSource}
                                />
                            </TableCell>
                            <TableCell>
                                <AttributionValue
                                    value={resource.attribution.service}
                                    source={resource.attribution.serviceSource}
                                />
                            </TableCell>
                            <TableCell>
                                <AttributionValue
                                    value={
                                        resource.attribution.environment === null
                                            ? null
                                            : t(`environment.${resource.attribution.environment}`)
                                    }
                                    source={resource.attribution.environmentSource}
                                />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
